// AccountActions.tsx
import React, { useRef, useEffect } from "react";
import { styled, Tooltip, Button, Badge } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircleOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCartOutlined";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { DrawerModal } from "./DrawerModal"; // Import the DrawerModal component
import MoreInfo from "./MoreInfo";
import Cart from "./Cart";
import Login from "./Login";
import { useLocation, useNavigate } from "react-router-dom";
import SnackbarAlert, { SnackbarRef } from "./SnackbarAlert";
import { getLoginCookieAndSession } from "../../utils";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { fetchCartProducts, fetchCartReset, PAGE_SIZE } from "../../redux/actions/cartAction";
import { useStripeAccountStatus } from "../../hooks/useStripeAccountStatus";

const CircularButton = styled(Button)({
  borderRadius: "50%",
  width: "40px",
  height: "40px",
  minWidth: "40px",
  padding: 0,
  margin: "0 4px",
  "&:hover": { backgroundColor: "#dedede" },
});

const AccountIconStyled = styled(AccountCircleIcon)({ fontSize: 30 });
const AddIconStyled = styled(AddCircleOutlineIcon)({ fontSize: 30 });
const CartIconStyled = styled(ShoppingCartIcon)({ fontSize: 28 });
const InfoIconStyled = styled(InfoIcon)({ fontSize: 30 });

const AccountActions: React.FC<{ onLogin: () => void; onSell: () => void }> = ({
  onLogin,
  onSell,
}) => {
  const snackbarRef = useRef<SnackbarRef>(null);
  const dispatch = useDispatch();
  const profileId = getLoginCookieAndSession();

  const location = useLocation();
  const navigate = useNavigate();
  const { sellRoute } = useStripeAccountStatus();

  const { cartData } = useSelector((state: RootState) => state.carts);
  const { products } = cartData;

  const [isAccountModalOpen, setAccountModalOpen] = React.useState(false);
  const [isSellModalOpen, setSellModalOpen] = React.useState(false);
  const [isCartModalOpen, setCartModalOpen] = React.useState(false);
  const [isInfoModalOpen, setInfoModalOpen] = React.useState(false);

  // Fetch cart count on mount and when profileId changes
  useEffect(() => {
    if (profileId) {
      dispatch(fetchCartProducts(Number(profileId), "", PAGE_SIZE) as any);
    } else {
      // Clear cart when user is not logged in
      dispatch(fetchCartReset());
    }
  }, [dispatch, profileId]);

  // Also check login status on location change (handles logout + navigation)
  useEffect(() => {
    const currentProfileId = getLoginCookieAndSession();
    if (!currentProfileId) {
      dispatch(fetchCartReset());
    }
  }, [dispatch, location]);

  // Calculate total cart items count - only show count if logged in
  const cartItemsCount = profileId
    ? products.reduce((acc, product) => acc + (product.cartQuantity || 1), 0)
    : 0;

  const openAccountModal = () => {
    if (getLoginCookieAndSession()) {
      navigate(`/account`);
    }
    setAccountModalOpen(true);
  };
  const closeAccountModal = () => setAccountModalOpen(false);

  const openSellModal = () => {
    if (getLoginCookieAndSession()) {
      navigate(sellRoute);
    }
    setSellModalOpen(true);
  };
  const closeSellModal = () => setSellModalOpen(false);

  const openCartModal = () => setCartModalOpen(true);
  const closeCartModal = () => setCartModalOpen(false);

  const openInfoModal = () => setInfoModalOpen(true);
  const closeInfoModal = () => setInfoModalOpen(false);

  React.useEffect(() => {
    setAccountModalOpen(false);
    setSellModalOpen(false);
    setCartModalOpen(false);
    setInfoModalOpen(false);
  }, [location]);

  const onDeleteCartItem = () => {
    snackbarRef.current?.showSnackbar("Item removed from cart", "success");
  };

  return (
    <>
      <DrawerModal
        isOpen={isAccountModalOpen}
        onClose={closeAccountModal}
        anchor="bottom"
        onOpen={openAccountModal}
      >
        <Login openFormFrom={"User"} />
      </DrawerModal>

      <DrawerModal
        isOpen={isSellModalOpen}
        onClose={closeSellModal}
        anchor="bottom"
        onOpen={openSellModal}
      >
        <Login openFormFrom={"Seller"} />
      </DrawerModal>

      <DrawerModal
        isOpen={isCartModalOpen}
        onClose={closeCartModal}
        anchor="bottom"
        onOpen={openCartModal}
      >
        <Cart onDeleteCartItem={onDeleteCartItem} isOpen={isCartModalOpen} />
      </DrawerModal>

      <DrawerModal
        isOpen={isInfoModalOpen}
        onClose={closeInfoModal}
        anchor="bottom"
        onOpen={openInfoModal}
      >
        <MoreInfo />
      </DrawerModal>

      <Tooltip title="Account" arrow placement="top">
        <CircularButton color="inherit" onClick={openAccountModal}>
          <AccountIconStyled />
        </CircularButton>
      </Tooltip>

      <Tooltip title="Sell" arrow placement="top">
        <CircularButton color="inherit" onClick={openSellModal}>
          <AddIconStyled />
        </CircularButton>
      </Tooltip>

      <Tooltip title="Shopping Cart" arrow placement="top">
        <CircularButton color="inherit" onClick={openCartModal}>
          <Badge
            badgeContent={cartItemsCount}
            color="primary"
            sx={{
              "& .MuiBadge-badge": {
                color: "white", // Set the text color to white
              },
            }}
          >
            <CartIconStyled />
          </Badge>
        </CircularButton>
      </Tooltip>

      <Tooltip title="Info" arrow placement="top">
        <CircularButton color="inherit" onClick={openInfoModal}>
          <InfoIconStyled />
        </CircularButton>
      </Tooltip>
      <SnackbarAlert ref={snackbarRef} />
    </>
  );
};

// Export CircularButton
export { CircularButton };

export default AccountActions;
